import Link from "next/link";
import { getPathForPage, navGroups } from "@/config/navigation";
import type { Page } from "@/types";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarRail, useSidebar } from "@/components/layout/Sidebar/sidebar";
import { ChevronsUpDown, ChevronRight, Folder, LogOut, Settings } from "lucide-react";

export function AppSidebar({
  active,
  onChange,
  authUser,
  onLogout,
  onOpenSettings,
}: {
  active: Page;
  onChange: (page: Page) => void;
  authUser: string;
  onLogout: () => void;
  onOpenSettings: () => void;
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const changePageFromSidebar = (page: Page) => {
    onChange(page);
    setOpenMobile(false);
  };

  return (
    <ShadcnSidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg">
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">Panel Jarvis</span>
                <span className="truncate text-xs">Контрольная панель</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Навигация</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navGroups.map((group) => (
                <SidebarMenuItem key={group.title}>
                  <Collapsible defaultOpen className="group/collapsible">
                    <SidebarMenuButton render={<CollapsibleTrigger />} tooltip={group.title}>
                      <Folder />
                      <span>{group.title}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-open/collapsible:rotate-90" />
                    </SidebarMenuButton>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <SidebarMenuSubItem key={item.id}>
                              <SidebarMenuSubButton
                                render={<Link href={getPathForPage(item.id)} />}
                                isActive={active === item.id}
                                aria-current={active === item.id ? "page" : undefined}
                                onClick={() => changePageFromSidebar(item.id)}
                              >
                                <Icon />
                                <span>{item.label}</span>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground" aria-label="Открыть профиль">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">{(authUser || "admin").slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{authUser || "admin"}</span>
                    <span className="truncate text-xs">Администратор</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side={isMobile ? "bottom" : "right"} align="end" sideOffset={4} className="w-(--anchor-width) min-w-56 rounded-lg">
                <DropdownMenuItem onClick={onOpenSettings}>
                  <Settings />
                  Настройки
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onLogout}>
                  <LogOut />
                  Выйти
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </ShadcnSidebar>
  );
}
